import { useState } from "react";
import API from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Sparkle, Lightning, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

export function AiBriefButton({ workType, category, propertyAddress, onApply }) {
  const [open, setOpen] = useState(false);
  const [shortDesc, setShortDesc] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const generate = async () => {
    if (!shortDesc.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const { data } = await API.post("/ai/generate-brief", {
        description: shortDesc,
        work_type: workType,
        category: category || "",
        property_address: propertyAddress || "",
      });
      setResult(data);
    } catch {
      toast.error("AI generation failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const apply = () => {
    if (!result) return;
    onApply(result);
    toast.success("AI brief applied to form");
    setOpen(false);
    setShortDesc("");
    setResult(null);
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-7 text-[10px] gap-1 border-violet-300 text-violet-700 hover:bg-violet-50 dark:border-violet-700 dark:text-violet-400 dark:hover:bg-violet-900/20"
        onClick={() => setOpen(true)}
        data-testid="ai-brief-btn"
      >
        <Sparkle size={12} weight="fill" /> Generate with AI
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="ai-brief-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo] flex items-center gap-2">
              <Sparkle size={18} className="text-violet-500" weight="fill" /> AI Brief Generator
            </DialogTitle>
            <DialogDescription>Describe the work in a few words and AI will generate a detailed professional brief.</DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <Label>Describe the work needed</Label>
              <Input
                value={shortDesc}
                onChange={e => setShortDesc(e.target.value)}
                placeholder="e.g. kitchen tap leaking, boiler not heating, broken window latch..."
                data-testid="ai-brief-input"
                onKeyDown={e => e.key === "Enter" && !loading && generate()}
              />
            </div>

            <Button
              onClick={generate}
              disabled={loading || !shortDesc.trim()}
              className="w-full bg-violet-600 hover:bg-violet-700"
              data-testid="ai-brief-generate-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2"><Lightning size={14} className="animate-pulse" /> Generating...</span>
              ) : (
                <span className="flex items-center gap-2"><Sparkle size={14} weight="fill" /> Generate Brief</span>
              )}
            </Button>

            {result && (
              <div className="border border-border rounded-lg p-3 space-y-3 bg-muted/30" data-testid="ai-brief-result">
                <div>
                  <p className="text-[10px] text-muted-foreground font-semibold uppercase mb-1">Title</p>
                  <p className="text-sm font-medium">{result.title}</p>
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground font-semibold uppercase mb-1">Description</p>
                  <p className="text-xs text-muted-foreground whitespace-pre-line leading-relaxed">{result.description}</p>
                </div>
                {result.safety_notes && (
                  <div>
                    <p className="text-[10px] text-muted-foreground font-semibold uppercase mb-1">Safety Notes</p>
                    <p className="text-xs text-amber-600">{result.safety_notes}</p>
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  {result.suggested_category && (
                    <Badge variant="secondary" className="text-[9px]">Category: {result.suggested_category}</Badge>
                  )}
                  {result.priority_suggestion && (
                    <Badge variant="secondary" className="text-[9px]">Priority: {result.priority_suggestion}</Badge>
                  )}
                  {result.estimated_duration && (
                    <Badge variant="secondary" className="text-[9px]">Duration: {result.estimated_duration}</Badge>
                  )}
                </div>
                {result.key_requirements?.length > 0 && (
                  <div>
                    <p className="text-[10px] text-muted-foreground font-semibold uppercase mb-1">Key Requirements</p>
                    <div className="space-y-1">
                      {result.key_requirements.map((r, i) => (
                        <div key={i} className="flex items-start gap-1.5 text-[11px] text-muted-foreground">
                          <CheckCircle size={12} className="text-emerald-500 shrink-0 mt-0.5" weight="fill" />
                          {r}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => { setOpen(false); setResult(null); }}>Cancel</Button>
            {result && (
              <Button onClick={apply} className="bg-violet-600 hover:bg-violet-700" data-testid="ai-brief-apply-btn">
                Apply to Form
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function AiSmartAssignButton({ category, title, description, area, onSelect }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recommendations, setRecommendations] = useState([]);

  const suggest = async () => {
    setLoading(true);
    setRecommendations([]);
    try {
      const { data } = await API.post("/ai/smart-assign", { category, title, description, area });
      setRecommendations(data.recommendations || []);
      if (!data.recommendations?.length) toast.info("No matching suppliers found");
    } catch {
      toast.error("AI suggestion failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-7 text-[10px] gap-1 border-teal-300 text-teal-700 hover:bg-teal-50 dark:border-teal-700 dark:text-teal-400 dark:hover:bg-teal-900/20"
        onClick={() => { setOpen(true); suggest(); }}
        data-testid="ai-smart-assign-btn"
      >
        <Lightning size={12} weight="fill" /> AI Suggest Supplier
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="ai-smart-assign-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo] flex items-center gap-2">
              <Lightning size={18} className="text-teal-500" weight="fill" /> Smart Supplier Match
            </DialogTitle>
            <DialogDescription>AI-ranked suppliers based on skills, ratings, and job history.</DialogDescription>
          </DialogHeader>

          {loading && (
            <div className="text-center py-8">
              <Lightning size={24} className="mx-auto text-teal-500 animate-pulse mb-2" />
              <p className="text-xs text-muted-foreground">Analysing suppliers...</p>
            </div>
          )}

          {!loading && recommendations.length === 0 && (
            <p className="text-xs text-muted-foreground text-center py-8">No recommendations available</p>
          )}

          {!loading && recommendations.length > 0 && (
            <div className="space-y-2" data-testid="ai-recommendations-list">
              {recommendations.map((rec, i) => (
                <div
                  key={i}
                  className="border border-border rounded-md p-3 hover:border-teal-300 dark:hover:border-teal-700 transition-colors cursor-pointer"
                  onClick={() => { onSelect(rec); setOpen(false); toast.success(`${rec.supplier_name} selected`); }}
                  data-testid={`ai-recommendation-${i}`}
                >
                  <div className="flex items-start justify-between mb-1">
                    <div>
                      <p className="text-sm font-medium">{rec.vendor_name}</p>
                      <p className="text-[10px] text-muted-foreground">{rec.trade}</p>
                    </div>
                    <Badge
                      className="text-[9px]"
                      style={{
                        background: rec.match_score >= 80 ? "#dcfce7" : rec.match_score >= 60 ? "#fef9c3" : "#fee2e2",
                        color: rec.match_score >= 80 ? "#166534" : rec.match_score >= 60 ? "#854d0e" : "#991b1b",
                      }}
                    >
                      {rec.match_score}% match
                    </Badge>
                  </div>
                  <p className="text-[11px] text-muted-foreground">{rec.reason}</p>
                  {(rec.email || rec.phone) && (
                    <p className="text-[10px] text-muted-foreground mt-1">{[rec.email, rec.phone].filter(Boolean).join(" | ")}</p>
                  )}
                </div>
              ))}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
